// Agent seats, minted and revoked here.
//
// A seat is a narrow credential: it can propose a change and nothing else. It
// cannot write the log, so nothing an agent produces reaches a client without a
// person approving it in Staging and the log recording that person.
//
// The token is shown once. Not out of ceremony — a secret you can re-read is a
// secret nobody rotates, and it ends up pasted into somewhere that outlives the
// seat. Revoking is immediate and is the intended fix for a lost one.

import { useEffect, useState } from 'react'

import { Button, ConfirmAction, Section } from '../components/common'
import { Icon } from '../components/Icon'
import { api, workspaceSlug } from '../lib/api'
import { relativeShort } from '../lib/format'
import { useStore } from '../lib/store'

interface Seat {
  id: string
  name: string
  createdAt: string
  revokedAt: string | null
}

export function AgentSeatsPanel() {
  const { admin } = useStore()
  const [seats, setSeats] = useState<Seat[]>([])
  const [name, setName] = useState('')
  const [minted, setMinted] = useState<{ name: string; token: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState('')
  const slug = workspaceSlug()

  const load = () =>
    api
      .agentSeats()
      .then((r) => setSeats(r.seats))
      .catch(() => undefined)

  useEffect(() => {
    void load()
  }, [])

  if (!admin) return null

  async function mint() {
    if (!name.trim()) return
    setBusy(true)
    setError('')
    try {
      const r = await api.mintAgentSeat(name.trim())
      setMinted(r)
      setName('')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not mint a seat.')
    } finally {
      setBusy(false)
    }
  }

  const live = seats.filter((s) => !s.revokedAt)

  return (
    <Section
      title="Agent seats"
      note="they propose; you decide"
      right={
        <span className="flex flex-wrap items-center gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void mint()}
            placeholder="What is it for?"
            className="rounded-control text-body border px-2.5 py-1 outline-none"
            style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
          />
          <Button variant="work" onClick={() => void mint()}>
            <Icon name="Plus" size={14} /> {busy ? 'Minting…' : 'New seat'}
          </Button>
        </span>
      }
    >
      {error && (
        <p className="text-body mb-2" style={{ color: 'var(--crit)' }}>
          {error}
        </p>
      )}

      {minted && (
        <div
          className="rounded-control mb-3 border px-3 py-2.5"
          style={{
            borderColor: 'color-mix(in srgb, var(--good) 40%, transparent)',
            background: 'color-mix(in srgb, var(--good) 8%, transparent)',
          }}
        >
          <p className="text-body">
            <b>{minted.name}</b> — copy this now, it is not shown again.
          </p>
          <code
            className="rounded-inline text-label mt-1.5 block overflow-x-auto border px-2 py-1.5"
            style={{
              borderColor: 'var(--border)',
              background: 'var(--panel)',
              fontFamily: 'var(--mono)',
            }}
          >
            {minted.token}
          </code>
          <p className="meta mt-1.5">
            The agent sends it as <code>Authorization: Bearer …</code> to{' '}
            <code>POST /api/w/{slug}/suggestions</code>. It can do nothing else.
          </p>

          {/* The key on its own is not enough to brief an agent, and an agent briefed
              by guesswork proposes duplicates and rows nobody asked for. This is the
              whole briefing, and the one thing it has to carry is the URL of
              /llms.txt — which is the contract, kept next to the code that enforces
              it rather than retyped here. */}
          <p className="meta mt-3 mb-1">Paste this to the agent:</p>
          <textarea
            readOnly
            rows={7}
            onFocus={(e) => e.currentTarget.select()}
            value={prompt(minted.token, slug, minted.name)}
            className="rounded-inline text-label w-full resize-y border px-2 py-1.5 leading-relaxed"
            style={{
              borderColor: 'var(--border)',
              background: 'var(--panel)',
              fontFamily: 'var(--mono)',
            }}
          />
          <span className="mt-1.5 flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(prompt(minted.token, slug, minted.name))
                setCopied(true)
                window.setTimeout(() => setCopied(false), 1400)
              }}
              className="meta underline"
            >
              {copied ? 'copied' : 'copy the briefing'}
            </button>
            <button type="button" onClick={() => setMinted(null)} className="meta underline">
              done
            </button>
          </span>
        </div>
      )}

      {live.length === 0 ? (
        <p className="note py-1">No seats. Agents cannot reach this deployment.</p>
      ) : (
        <div className="rows">
          {live.map((s) => (
            <div key={s.id} className="flex flex-wrap items-baseline gap-x-3 py-2">
              <span className="text-body font-medium">{s.name}</span>
              <span className="meta">····{s.id}</span>
              <span className="meta ml-auto">minted {relativeShort(s.createdAt)}</span>
              <ConfirmAction
                icon="Trash2"
                title={`Revoke ${s.name}`}
                confirmLabel="Revoke"
                onConfirm={() => void api.revokeAgentSeat(s.id).then(load)}
              />
            </div>
          ))}
        </div>
      )}

      <p className="note mt-2">
        A seat can read the log and propose changes to it. It cannot write one: what it proposes
        waits in Staging until someone takes it, row by row, and the log then records that person
        rather than the agent. The full contract an agent needs — the event envelope, the
        vocabulary, and the refusals — is at{' '}
        <a href="/llms.txt" target="_blank" rel="noreferrer" className="underline">
          /llms.txt
        </a>
        .
      </p>
    </Section>
  )
}

/**
 * The briefing an agent is given along with its key.
 *
 * It is deliberately short and does almost nothing except point at `/llms.txt`,
 * which is served from this deployment and kept next to the code that enforces it.
 * Restating the contract here would be a second copy to keep in step, and the day
 * they drift is the day an agent proposes against rules that no longer hold.
 */
function prompt(key: string, slug: string, name: string): string {
  const base = location.origin
  return [
    `You have an agent seat in crum, a team CRM, named "${name}".`,
    ``,
    `Read ${base}/llms.txt FIRST. It is the whole contract: what your seat may do,`,
    `the event envelope, the vocabulary, and the refusals. Follow it exactly.`,
    ``,
    `Workspace slug: ${slug}`,
    `Base URL:       ${base}`,
    `Credential:     Authorization: Bearer ${key}`,
    ``,
    `You can read the log and PROPOSE changes to it. You cannot write to it: what you`,
    `propose waits for a person to accept it, row by row. Read before you propose.`,
  ].join('\n')
}
